import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { publicApi } from "@/lib/api";
import {
  ArrowLeft,
  Copy,
  Check,
  ExternalLink,
  Users,
  Clock,
  Shield,
} from "lucide-react";

export default function JoinPage() {
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    publicApi.publicStats().then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const appUrl = window.location.origin;

  const whatsappMsg = `Jai Shree Ram! The Aylesbury Hindu Temple is asking all community members to participate in the Buckinghamshire Council Local Plan consultation.\n\nIt only takes 10-15 minutes: ${appUrl}\n\nYour responses are pre-filled — just review and submit from your own device. Every voice strengthens our community's representation.\n\nFor more info: www.aylesburyhindutemple.org`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(appUrl);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = appUrl;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMsg)}`;

  return (
    <div className="public-page">
      <header className="public-header">
        <div className="public-header-inner">
          <div className="guide-header-row">
            <div className="public-logo" style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img src="/aht_round_orange.png" alt="AHT" style={{ height: 36, width: 36, borderRadius: "50%" }} />
              <span className="dash-logo-text">Aylesbury Hindu Temple</span>
            </div>
            <Link to="/" className="guide-back-link">
              <ArrowLeft size={14} /> Back to Generator
            </Link>
          </div>
        </div>
      </header>

      <main className="join-page">
        <div className="join-hero">
          <h1>Help Your Community Be Heard</h1>
          <p>
            Buckinghamshire Council is planning new developments across Aylesbury.
            The Aylesbury Hindu Temple is coordinating our community's response to ensure
            we have proper representation in this important consultation.
          </p>
        </div>

        {/* Social proof stats */}
        {stats && (
          <div className="join-stats-row">
            <div className="join-stat">
              <strong>{stats.total_supporters}</strong>
              <span>Supporters</span>
            </div>
            <div className="join-stat">
              <strong>{stats.total_submissions}</strong>
              <span>Submissions</span>
            </div>
            <div className="join-stat">
              <strong>{stats.completion_rate}%</strong>
              <span>Completion Rate</span>
            </div>
          </div>
        )}

        {/* Key selling points */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px", margin: "24px 0" }}>
          <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
            <Clock size={20} style={{ color: "var(--cf-green)", flexShrink: 0, marginTop: 2 }} />
            <div>
              <strong>10-15 minutes</strong>
              <p style={{ color: "var(--cf-muted)", fontSize: "14px", margin: 0 }}>
                Pre-filled responses for all sites. Just review, personalise, and submit.
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
            <Shield size={20} style={{ color: "var(--cf-green)", flexShrink: 0, marginTop: 2 }} />
            <div>
              <strong>Privacy-preserving</strong>
              <p style={{ color: "var(--cf-muted)", fontSize: "14px", margin: 0 }}>
                No personal data stored. Each response is submitted from your own device.
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
            <Users size={20} style={{ color: "var(--cf-green)", flexShrink: 0, marginTop: 2 }} />
            <div>
              <strong>Every voice counts</strong>
              <p style={{ color: "var(--cf-muted)", fontSize: "14px", margin: 0 }}>
                The more people who respond, the stronger our community's representation.
              </p>
            </div>
          </div>
        </div>

        {/* CTA buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "24px" }}>
          <Link to="/" className="btn-primary gen-btn" style={{ textAlign: "center", textDecoration: "none" }}>
            Start My Response
          </Link>

          <div className="share-buttons">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary gen-btn"
              style={{ background: "#25D366", borderColor: "#25D366", textDecoration: "none", textAlign: "center" }}
            >
              Share on WhatsApp
            </a>
            <button className="btn-secondary gen-btn" onClick={handleCopyLink}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
              <span>{copied ? "Link Copied!" : "Copy Link"}</span>
            </button>
          </div>
        </div>

        {/* Outreach link */}
        <div style={{ textAlign: "center", marginTop: "24px" }}>
          <Link to="/outreach" style={{ color: "var(--cf-orange)", fontSize: "14px" }}>
            Community leaders: Get ready-made outreach templates <ExternalLink size={12} />
          </Link>
        </div>

        <div style={{ textAlign: "center", marginTop: "32px", padding: "16px 0", borderTop: "1px solid var(--cf-card-border)", color: "var(--cf-muted)", fontSize: 13 }}>
          <p style={{ margin: 0 }}>
            An initiative by <strong style={{ color: "var(--cf-orange)" }}>Aylesbury Hindu Temple &amp; Trust</strong> · <a href="https://www.aylesburyhindutemple.org" target="_blank" rel="noopener noreferrer" style={{ color: "var(--cf-orange)" }}>www.aylesburyhindutemple.org</a>
          </p>
          <p style={{ margin: "8px 0 0", fontSize: 11, opacity: 0.7 }}>
            Built by <a href="https://scaleright.ai/" target="_blank" rel="noopener noreferrer" style={{ color: "var(--cf-orange)" }}>ScaleRight.ai</a>
          </p>
        </div>
      </main>
    </div>
  );
}
